// File: FormValidation.ts

export async function validateForm(
  form: HTMLFormElement, 
  customValidations: ((form: HTMLFormElement) => boolean | Promise<boolean>)[] = []
): Promise<boolean> {
  let isValid = true;

  form.querySelectorAll("[required]").forEach((input) => {
    const inputElement = input as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
    const parentDiv = inputElement.closest(".dv-form-control"); // Adjust the selector to match your parent div class
    const errorMessage = inputElement.getAttribute("data-msg-required");

    const isVisible = !!(inputElement.offsetParent);

    // Skip validation if the parent div is marked as optional or parent div is not found or parent div is hidden
    if ((parentDiv && parentDiv.classList.contains("optional")) || !isVisible) {
      return;
    }

    // Check if the element is a radio button group
    if (inputElement.type === "radio") {
      // Find all radios with the same name
      const radioGroup = form.querySelectorAll(`input[name="${inputElement.name}"]`);
      const isChecked = Array.from(radioGroup).some((radio) => (radio as HTMLInputElement).checked);
    
      if (!isChecked) {
        isValid = false;
        displayErrorMessage(inputElement.closest("fieldset") as HTMLElement, errorMessage); // Display error for the fieldset
      } else {
        const radioErrorMessage = inputElement.closest("fieldset")?.querySelector(".error-message");
        if (radioErrorMessage) { 
          removeErrorMessage(radioErrorMessage as HTMLElement);
        }
      }
    } else {
      // For other required fields
      if (!inputElement.value) {
        isValid = false;
        displayErrorMessage(inputElement, errorMessage);
      } else {
        removeErrorMessage(inputElement);
      }
    }
  });

  // Apply custom validations (if any)
  for (const validation of customValidations) {
    const result = await validation(form); // Await asynchronous validations
    if (!result) {
      isValid = false;
    }
  }

  const firstError = form.querySelector(".error-message, .custom-error:not(.hidden)") as HTMLElement;
  // Try to find the associated input or control element based on the error message
  let inputWithError: HTMLElement | null = null;

  // Check if the error message is linked to a specific field
  if (firstError) {
    // If the error message is directly next to an input, use that input
    inputWithError = firstError.previousElementSibling as HTMLElement;

    // If no input is found in the previous sibling, search up the DOM hierarchy for the nearest form control
    if (!inputWithError) {
      const formControl = firstError.closest('.dv-form-control') || firstError.closest('div'); // Fallback to div if no specific form control class
      inputWithError = formControl ? formControl.querySelector("input, select, textarea, radio") as HTMLElement : null;
    }
  }

  if (!isValid && firstError) {
    const header = document.querySelector("header");
    const headerHeight = header ? header.clientHeight : 0;
    const targetPosition =
    inputWithError!.getBoundingClientRect().top + window.scrollY - headerHeight - 40;

    if (form.id !== 'quizResultForm' && form.id !== 'riskQuizResultForm') {
      window.scrollTo({
        top: targetPosition,
        behavior: "smooth",
      });
    }

    // Focus without triggering an automatic scroll
    setTimeout(() => {
      inputWithError!.focus({ preventScroll: true });
    }, 500);
  }

  return isValid;
}

export function displayErrorMessage(input: HTMLElement, message: string | null) {
  if (!message || input.nextElementSibling?.classList.contains("error-message")) return;

  const errorDiv = document.createElement("div");
  errorDiv.setAttribute('role', 'alert');
  errorDiv.className = "error-message";
  errorDiv.innerHTML = `Error: ${message}`;
  const inputDataAriaDescription = input.getAttribute('data-aria-describedby');
  if (inputDataAriaDescription) {
    const inputAriaDescription = input.getAttribute('aria-describedby');
    if (inputAriaDescription) {
      // multiple aria-describedby id, append data-aria-describedby
      input.setAttribute('aria-describedby', `${inputAriaDescription} ${inputDataAriaDescription}`);
    } else {
      // set aria-describedby on input
      input.setAttribute('aria-describedby', inputDataAriaDescription);
    }
    errorDiv.id = inputDataAriaDescription;
  }
  input.insertAdjacentElement("afterend", errorDiv);
  input.focus();
}

export function removeErrorMessage(input: HTMLElement) {
  const inputAriaDescription = input.getAttribute('aria-describedby');
  if (inputAriaDescription && inputAriaDescription.includes(`${input.id}Error`)) {
    // filter out non-custom error message
    const updatedAriaDescription = inputAriaDescription.split(" ").filter(a => a !== `${input.id}-msg`).join(" ");
    input.setAttribute('aria-describedby', updatedAriaDescription);
  } else {
    // no custom error messages, remove aria-describedby attribute
    input.removeAttribute('aria-describedby');
  }

  // Find the closest parent element (like the container div)
  const parent = input.closest("div");

  if (parent) {
    // Look for the error message within the parent container
    const errorDiv = parent.querySelector(".error-message");
    if (errorDiv) {
      errorDiv.remove();
    }
  }
}

export function handleFocus(event: FocusEvent) {
  const target = event.target as HTMLElement;
  removeErrorMessage(target);
}

export function validateEmail(input: HTMLInputElement): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const email = input.value.trim();

  if (email === "") {
    return false;
  }

  // If the email is invalid, show the invalid email message
  if (!emailRegex.test(email) && input.value.length > 0) {
    displayErrorMessage(input, input.getAttribute('data-msg-email') || 'PLEASE ENTER A VALID EMAIL ADDRESS.');
    return false;
  }

  return true;
}

export function validateZipCode(input: HTMLInputElement): boolean {
  const countryField = document.getElementById("country") as HTMLSelectElement | null;
  const zipCode = input.value.trim();
  const zipCodeRegex = /^[0-9]{5}$/;

  if (countryField && countryField.value === 'US') {
    if (zipCode === '') {
      displayErrorMessage(input, input.getAttribute('data-msg-required') || 'ONLY REQUIRED FOR THE US.');
      return false;
    }

    if (!zipCodeRegex.test(zipCode)) {
      displayErrorMessage(input, input.getAttribute('data-msg-zipcode') || 'PLEASE ENTER A VALID ZIP CODE.');
      return false;
    }

    return true;
  }

  if (zipCode !== '' && !zipCodeRegex.test(zipCode)) {
    displayErrorMessage(input, input.getAttribute('data-msg-zipcode') || 'PLEASE ENTER A VALID ZIP CODE.');
    return false;
  }

  return true;
}

export function handleDobValidation(input: HTMLInputElement): boolean {
  // Ensure the format is YYYY-MM-DD and the length is correct
  const dobPattern = /^\d{4}-\d{2}-\d{2}$/;
  let dobString = input.value.trim();
  let isValid = true;

  if (!dobPattern.test(dobString)) {
    isValid = false; // Invalid format
  }

  const [year, month, day] = dobString.split('-').map(Number);

  // Ensure the date is logically valid (e.g., February 30th is invalid)
  const inputDate = new Date(year, month - 1, day);
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Set to the start of the day for comparison

  // The date can't be in the future
  if (inputDate > today) {
    isValid = false;
  }

  if (inputDate.getFullYear() !== year || inputDate.getMonth() !== month - 1 || inputDate.getDate() !== day) {
    isValid = false;
  }

  if (input.value === "") {
    return false;
  }

  if (isValid === false && input.value.length > 0) {
    displayErrorMessage(input, input.getAttribute("data-msg-invalid") || 'PLEASE ENTER A VALID DATE.');
    return false;
  }

  return true;
}

export function validatePhoneNumber(input: HTMLInputElement): boolean {
  const phoneNumber = input.value.replace(/\D/g, '');  // Remove non-digits
  const isValid = phoneNumber.length === 10;
  const isRequired = input.hasAttribute('required') || input.getAttribute('data-required') === 'true';  // Check if required

  // If the field is required and the input is empty, show the "required" error message
  if (isRequired && phoneNumber === "") {
    return false;  // Return false because it's empty and required
  }

  // If the phone number is not valid (not 10 digits), show the "invalid phone number" error
  if (isValid === false && input.value.length > 0) {
    displayErrorMessage(input, input.getAttribute('data-msg-phone') || 'PLEASE ENTER A VALID PHONE NUMBER.');
    return false;
  }

  // If valid, clear any previous error messages
  return true;
}
